import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageSquare, FiCpu, FiUser, FiInfo } = FiIcons;

const ChatSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    // Load the chatbot script
    const script = document.createElement('script');
    script.src = 'https://emb-chat.noform.ai/snippet-bundle.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);

    // Function to scroll to top
    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Listen for messages from the iframe (common in chat embeds)
    // and handle focus/scroll events
    const handleMessage = (event) => {
      // Many chat providers send messages on 'message sent' or 'typing'
      // We trigger a scroll to top to ensure the user sees the response
      if (event.data) {
        scrollToTop();
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Also scroll to top if the window is resized (like when keyboard opens/closes)
    const handleResize = () => {
      if (document.activeElement.tagName === 'IFRAME') {
        scrollToTop();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section 
      id="chatbot" 
      ref={sectionRef}
      className="pt-0 pb-12 md:pb-20 bg-white"
    >
      <div className="max-w-6xl mx-auto px-0 sm:px-6 lg:px-8">
        
        <div className="grid lg:grid-cols-12 gap-0 lg:gap-8 items-start">
          {/* Main Chatbot Container - Full Width on Mobile */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-8 order-1 bg-white shadow-sm lg:shadow-xl lg:rounded-2xl overflow-hidden border-b lg:border border-gray-100"
          >
            {/* Minimal Status Bar */}
            <div className="bg-white px-4 py-2 border-b border-gray-50 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-[9px] md:text-xs font-bold text-gray-400 uppercase tracking-widest">
                  AI Assistant Active
                </span>
              </div>
              <SafeIcon icon={FiMessageSquare} className="text-blue-500 w-3 h-3 md:w-4 md:h-4" />
            </div>
            
            {/* Chat Iframe with Dynamic Height */}
            <div className="relative w-full bg-white flex justify-center">
              <iframe 
                id="ai-chat-frame" 
                src="https://emb-chat.noform.ai/?pid=674c80fb19fe0b56e8f1b185&backendURL=https://app.noform.ai" 
                className="w-full h-[70vh] md:h-[700px] lg:h-[750px]"
                style={{ border: 'none' }}
                title="Jonas Jaani AI Chatbot"
              />
            </div>
          </motion.div>

          {/* Sidebar Info - Hidden or moved down on mobile to prioritize chat */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-4 order-2 p-6 lg:p-0 space-y-4"
          >
            {/* Simple Welcome */}
            <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                <SafeIcon icon={FiInfo} className="text-blue-600 w-4 h-4" />
                Information
              </h3>
              <p className="text-gray-600 text-xs leading-relaxed">
                Ställ frågor om IT-strategi, AI och digital transformation. Svaren baseras på Jonas Jaani's expertis.
              </p>
            </div>

            {/* Links */}
            <div className="flex flex-col gap-2">
              <a 
                href="https://www.jonasjaani.se" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-xl hover:bg-blue-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCpu} className="text-blue-600 w-4 h-4" />
                  <span className="text-xs font-bold text-gray-700">JonasJaani.se</span>
                </div>
                <span className="text-blue-600 group-hover:translate-x-1 transition-transform">→</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ChatSection;